import React from 'react';

import { Alert, Center, Divider, LoadingOverlay, Space, Stack, useMantineTheme } from '@mantine/core';
import { IconExclamationCircle } from '@tabler/icons-react';
import AthleteClassStateStack from 'components/AthleteClass/AthleteClassStateStack';
import { useAuth } from 'context/auth-context';
import useAthleteClassMeta from 'hooks/athleteClass/useAthleteClassMeta';
import useAthleteClassState from 'hooks/athleteClass/useAthleteClassState';
import useClassAthleteRetrieve from 'hooks/class/useClassAthleteRetrieve';
import { useClassMeta } from 'hooks/class/useClassMeta';
import useGymAthleteRetrieve from 'hooks/gym/useGymAthleteRetrieve';
import usePrograms from 'hooks/program/usePrograms';
import BackHeader from 'layouts/MobileLayout/components/BackHeader';
import { isWorkoutTrackerEnabled } from 'utils/workoutTracker';

import AthleteWorkoutSection from './Class/AthleteWorkoutSection';
import CancelReservationSection from './Class/CancelReservationSection';
import ClassMainSection from './Class/ClassMainSection';
import LeaveWaitlistSection from './Class/LeaveWaitlistSection';
import WorkoutProgramSection from './Class/WorkoutProgramSection';
import AthleteListSection from './shared/AthleteListSection';
import HeaderSection from './shared/HeaderSection';

interface ClassDetailProps {
  eventId: string;
}

const ClassDetail: React.FC<ClassDetailProps> = ({ eventId }) => {
  const theme = useMantineTheme();
  const { session } = useAuth();
  const { data: gymData } = useGymAthleteRetrieve(session?.gym?.gymId);
  const hideReservedAthletes = gymData?.data?.athleteAppHideReservedAthletes ?? false;
  const workoutTrackerEnabled = isWorkoutTrackerEnabled(session?.gym?.workoutTrackerEnabled);

  const {
    data: classData,
    isLoading: isClassDataLoading,
    isError: isClassError,
  } = useClassAthleteRetrieve(eventId);

  const athleteKiloClass = classData?.data.athleteKiloClasses.find(
    akc => akc.athlete?.athleteId === session?.athlete?.athleteId,
  );

  const athleteClassState = useAthleteClassState(athleteKiloClass);
  const classMeta = useClassMeta(classData?.data);
  const athleteClassMeta = useAthleteClassMeta(
    athleteClassState,
    classMeta,
    athleteKiloClass?.waitlistOrder || undefined,
  );

  const { data: programs } = usePrograms({
    kilo_class_type: workoutTrackerEnabled ? classData?.data.kiloClassType.kiloClassTypeId : undefined,
    date: workoutTrackerEnabled ? classData?.data.startDate : undefined,
  });
  const legacyNotes = programs?.pages[0]?.data.results[0]?.workoutNotes ?? null;

  if (isClassError) {
    return (
      <>
        <BackHeader title='Class Details' />
        <Center sx={{ flexGrow: 1 }} p='md'>
          <Alert icon={<IconExclamationCircle size={theme.fontSizes.md} />} title='Error' color='red'>
            Something went wrong while fetching class data.
          </Alert>
        </Center>
      </>
    );
  }

  if (!classData?.data) {
    return (
      <>
        <BackHeader title='Class Details' />
        <LoadingOverlay visible={isClassDataLoading} />
      </>
    );
  }

  const { data: classInfo } = classData;
  const eventCoachRoles = classInfo.kiloClassCoachKiloClassCoachRoles.map(ccccr => ({
    coach: ccccr.coach,
    role: ccccr.kiloClassCoachRole.name,
  }));

  const renderCancelAction = (): JSX.Element | null => {
    if (!athleteKiloClass || !athleteClassMeta.cancelAction) return null;

    if (athleteClassMeta.cancelAction === 'leaveWaitlist') {
      return (
        <LeaveWaitlistSection
          classData={classInfo}
          athleteKiloClassId={athleteKiloClass.athleteKiloClassId}
          classStartDateTime={classMeta.eventStartDateTime}
          waitlistOrder={athleteKiloClass.waitlistOrder}
        />
      );
    }

    return (
      <CancelReservationSection
        classData={classInfo}
        athleteKiloClassId={athleteKiloClass.athleteKiloClassId}
        action={athleteClassMeta.cancelAction}
      />
    );
  };

  return (
    <>
      <BackHeader title={`${classInfo.kiloClassType.name} Details`} />
      <Stack className='content'>
        <LoadingOverlay visible={isClassDataLoading} />

        <HeaderSection
          fullStartDate={classMeta.eventStartDateTime}
          duration={classInfo.duration}
          eventCoachEventCoachRoles={eventCoachRoles}
        />

        <AthleteClassStateStack athleteClassMeta={athleteClassMeta} kiloClass={classData.data} size='lg' />

        <Divider mx='-md' />

        <ClassMainSection classData={classInfo} classMeta={classMeta} />

        <Divider mx='-md' />

        {!hideReservedAthletes && <AthleteListSection athleteKiloClasses={classInfo.athleteKiloClasses} />}

        <Divider mx='-md' />

        {workoutTrackerEnabled ? (
          <AthleteWorkoutSection
            kiloClassId={classInfo.kiloClassId}
            athleteKiloClassId={athleteKiloClass?.athleteKiloClassId}
            hasStarted={classMeta.hasStarted}
            legacyNotes={legacyNotes}
          />
        ) : (
          <WorkoutProgramSection
            kiloClassTypeId={classInfo.kiloClassType.kiloClassTypeId}
            startDate={classInfo.startDate}
          />
        )}
        {renderCancelAction()}

        {session?.athlete?.activeOnWodup && <Space h={65} />}
      </Stack>
    </>
  );
};

export default ClassDetail;
